// src/components/LegalPage.js
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';

const TEXT = '#e5e7eb';
const SUBTLE = '#9ca3af';
const CARD_BG = '#1f2937';
const BORDER = 'rgba(255,255,255,0.08)';
const PAGE_BG = '#0b0b0b';

function parseFrontmatter(raw) {
  if (!raw) return { meta: {}, body: '' };
  const m = /^---\s*\n([\s\S]*?)\n---\s*\n?([\s\S]*)$/.exec(raw);
  if (!m) return { meta: {}, body: raw };
  const meta = {};
  m[1].split('\n').forEach((line) => {
    const idx = line.indexOf(':');
    if (idx === -1) return;
    const key = line.slice(0, idx).trim();
    const val = line.slice(idx + 1).trim().replace(/^["']|["']$/g, '');
    if (key) meta[key] = val;
  });
  return { meta, body: m[2] };
}

export default function LegalPage({ title, contentUrl, showLastUpdated = false }) {
  const [body, setBody] = useState('');
  const [meta, setMeta] = useState({});
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    fetch(contentUrl)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.text();
      })
      .then((text) => {
        if (!alive) return;
        const { meta: fm, body: md } = parseFrontmatter(text);
        setMeta(fm);
        setBody(md);
      })
      .catch((e) => {
        if (alive) setError(String(e?.message || e));
      });
    return () => {
      alive = false;
    };
  }, [contentUrl]);

  const mdComponents = {
    h1: ({ children, ...rest }) => (
      <h1 style={{ fontSize: 36, fontWeight: 800, margin: '4px 0 18px' }} {...rest}>
        {children}
      </h1>
    ),
    h2: ({ children, ...rest }) => (
      <h2 style={{ fontSize: 22, fontWeight: 700, margin: '28px 0 10px' }} {...rest}>
        {children}
      </h2>
    ),
    h3: ({ children, ...rest }) => (
      <h3 style={{ fontSize: 17, fontWeight: 700, margin: '22px 0 8px' }} {...rest}>
        {children}
      </h3>
    ),
    p: ({ children, ...rest }) => (
      <p
        style={{ margin: '10px 0', lineHeight: 1.65, color: TEXT, whiteSpace: 'pre-line' }}
        {...rest}
      >
        {children}
      </p>
    ),
    ul: ({ children, ...rest }) => (
      <ul style={{ margin: '8px 0 12px 22px', lineHeight: 1.6 }} {...rest}>
        {children}
      </ul>
    ),
    ol: ({ children, ...rest }) => (
      <ol style={{ margin: '8px 0 12px 22px', lineHeight: 1.6 }} {...rest}>
        {children}
      </ol>
    ),
    li: ({ children, ...rest }) => (
      <li style={{ margin: '4px 0' }} {...rest}>
        {children}
      </li>
    ),
    a: ({ children, href, ...rest }) => {
      const isExternal = !!href && /^https?:/.test(href);
      return (
        <a
          href={href}
          style={{ color: '#9AF68C', textDecoration: 'underline' }}
          target={isExternal ? '_blank' : undefined}
          rel={isExternal ? 'noopener noreferrer' : undefined}
          {...rest}
        >
          {children}
        </a>
      );
    },
    hr: () => (
      <hr style={{ border: 'none', borderTop: `1px solid ${BORDER}`, margin: '20px 0' }} />
    ),
    code: ({ children, ...rest }) => (
      <code
        style={{
          background: 'rgba(255,255,255,0.06)',
          padding: '1px 6px',
          borderRadius: 4,
          fontSize: '0.9em',
        }}
        {...rest}
      >
        {children}
      </code>
    ),
  };

  return (
    <div style={{ minHeight: '100vh', background: PAGE_BG, color: TEXT }}>
      <div style={{ maxWidth: 820, margin: '0 auto', padding: '24px 16px 8px' }}>
        <Link
          to="/"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            background: 'transparent',
            color: TEXT,
            border: `1px solid ${BORDER}`,
            borderRadius: 10,
            padding: '8px 12px',
            textDecoration: 'none',
            fontSize: 14,
          }}
        >
          <span aria-hidden="true">←</span>
          <span>Zurück</span>
        </Link>
      </div>

      <div style={{ maxWidth: 820, margin: '0 auto', padding: '12px 16px 60px' }}>
        <div
          style={{
            background: CARD_BG,
            border: `1px solid ${BORDER}`,
            borderRadius: 16,
            padding: '28px 32px',
            boxShadow: '0 10px 30px rgba(0,0,0,0.35)',
          }}
        >
          {error ? (
            <>
              <h1 style={{ fontSize: 28, fontWeight: 800, margin: '0 0 12px' }}>{title}</h1>
              <p style={{ color: SUBTLE }}>
                Inhalt konnte nicht geladen werden ({error}).
              </p>
            </>
          ) : !body ? (
            <p style={{ color: SUBTLE }}>Lade…</p>
          ) : (
            <ReactMarkdown components={mdComponents}>{body}</ReactMarkdown>
          )}

          {showLastUpdated && meta.lastUpdated && (
            <div
              style={{
                marginTop: 28,
                paddingTop: 14,
                borderTop: `1px solid ${BORDER}`,
                color: SUBTLE,
                fontSize: 13,
              }}
            >
              Letzte Aktualisierung: {meta.lastUpdated}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
